import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { MdChevronLeft, MdChevronRight, MdEditCalendar } from "react-icons/md";
import {
  adjacentReportingPeriodId,
  cmonthPeriodId,
  cweekPeriodId,
  rangePeriodId,
  reportingPeriodIdForKind,
  reportingPeriodKind,
  reportingPeriodRange,
  singleDayPeriodId,
} from "./reportingPeriod";

const CUSTOM_VALUE = "custom";

// Calendar-aligned steps (this week / this month) are re-derived from a period
// the backend has already resolved, so stepping them stays boundary-aligned.
// The rolling presets step by their own length. Only the "Last N days" rolling
// window (which has no fixed anchor yet) disables the forward step.
export default function ReportingPeriodControl({ period, periodId, onChange, compact = false }) {
  const kind = reportingPeriodKind(period?.id || periodId);
  const selectValue = kind === "range" ? CUSTOM_VALUE : kind;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [dayInput, setDayInput] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  // Seed the custom pickers from the active period each time the popover opens
  // so managers refine the current window rather than starting blank.
  useEffect(() => {
    if (!isOpen) return;
    const start = period?.start_date || "";
    const end = period?.end_date || "";
    setDayInput(end || start || "");
    setRangeStart(start);
    setRangeEnd(end);
  }, [isOpen, period?.start_date, period?.end_date]);

  const handleSelect = (value) => {
    if (value === "week" || value === "biweek") {
      onChange(reportingPeriodIdForKind(value, period));
    } else if (value === "cweek") {
      onChange(cweekPeriodId(new Date()));
    } else if (value === "cmonth") {
      onChange(cmonthPeriodId(new Date()));
    } else if (value === CUSTOM_VALUE) {
      onOpen();
    }
  };

  const applyDay = () => {
    const id = singleDayPeriodId(dayInput);
    if (id) {
      onChange(id);
      onClose();
    }
  };

  const applyRange = () => {
    const id = rangePeriodId(rangeStart, rangeEnd);
    if (id) {
      onChange(id);
      onClose();
    }
  };

  return (
    <Box minW={compact ? "220px" : "250px"}>
      <Text mb={1} fontSize="xs" color="gray.500" fontWeight="semibold">Reporting period</Text>
      <HStack spacing={1}>
        <Tooltip label="Previous reporting period" hasArrow>
          <IconButton
            size="sm"
            variant="outline"
            bg="white"
            icon={<MdChevronLeft />}
            aria-label="Previous reporting period"
            isDisabled={!period}
            onClick={() => onChange(adjacentReportingPeriodId(period, -1))}
          />
        </Tooltip>
        <Select
          size="sm"
          value={selectValue}
          onChange={(event) => handleSelect(event.target.value)}
          aria-label="Reporting period"
          bg="white"
        >
          <option value="week">7 days</option>
          <option value="biweek">14 days</option>
          <option value="cweek">This week</option>
          <option value="cmonth">This month</option>
          <option value={CUSTOM_VALUE}>Custom day or range…</option>
        </Select>
        <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end" isLazy>
          <Tooltip label="Pick a day or custom range" hasArrow>
            <Box as="span" display="inline-flex">
              <PopoverTrigger>
                <IconButton
                  size="sm"
                  variant="outline"
                  bg="white"
                  icon={<MdEditCalendar />}
                  aria-label="Pick a day or custom range"
                />
              </PopoverTrigger>
            </Box>
          </Tooltip>
          <PopoverContent w="272px">
            <PopoverArrow />
            <PopoverBody>
              <VStack align="stretch" spacing={3}>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>Single day</Text>
                  <HStack spacing={2}>
                    <Input size="sm" type="date" value={dayInput} onChange={(event) => setDayInput(event.target.value)} aria-label="Day" />
                    <Button size="sm" colorScheme="blue" onClick={applyDay} isDisabled={!dayInput}>Apply</Button>
                  </HStack>
                </Box>
                <Divider />
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>Custom range</Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack spacing={2}>
                      <Input size="sm" type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} aria-label="Range start" />
                      <Text fontSize="xs" color="gray.500">to</Text>
                      <Input size="sm" type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} aria-label="Range end" />
                    </HStack>
                    <Button size="sm" colorScheme="blue" alignSelf="flex-end" onClick={applyRange} isDisabled={!rangeStart || !rangeEnd}>Apply range</Button>
                  </VStack>
                  <Text mt={1} fontSize="xs" color="gray.400">Up to 90 days.</Text>
                </Box>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
        <Tooltip label="Next reporting period" hasArrow>
          <IconButton
            size="sm"
            variant="outline"
            bg="white"
            icon={<MdChevronRight />}
            aria-label="Next reporting period"
            isDisabled={!period || String(period.label || "").startsWith("Last ")}
            onClick={() => onChange(adjacentReportingPeriodId(period, 1))}
          />
        </Tooltip>
      </HStack>
      {period && <Text mt={1} fontSize="xs" color="gray.500">{reportingPeriodRange(period)}</Text>}
    </Box>
  );
}
